import { useEffect, useRef, useState } from 'react';
import firebase from '../auth/firebase';
import { isExplicitlyImpersonating } from '../auth/emailChange';
import { getEmailChangeCodeError, requestEmailChangeCode, verifyEmailChangeCode } from '../auth/emailChangeCode';
import { useAuthContext } from '../context/AuthContext';

export default function useEmailChangeCode({ open, userId, language, onClose }) {
  const auth = useAuthContext();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now);
  const [retryAt, setRetryAt] = useState(0);
  const [verified, setVerified] = useState(false);
  const recovery = useRef({});
  const generation = useRef(0);
  const pending = useRef(false);
  const uid = firebase.auth()?.currentUser?.uid;
  useEffect(() => {
    ++generation.current;
    pending.current = false;
    setBusy(false); setEmail(''); setCode(''); setChallenge(null); setError(''); setRetryAt(0);
    recovery.current = {}; setVerified(false);
    return () => { ++generation.current; };
  }, [open, userId, uid]);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);
  const resendIn = Math.max(0, Math.ceil((Math.max(Date.parse(challenge?.resendAt) || 0, retryAt) - now) / 1000));
  const expired = Boolean(!verified && challenge && Date.parse(challenge.expiresAt) <= now);
  const blocked = isExplicitlyImpersonating();
  const run = async action => {
    if (pending.current || blocked) return;
    if (!uid) { setError('email_change_auth_required'); return; }
    const id = ++generation.current;
    const isCurrent = () => generation.current === id && firebase.auth().currentUser?.uid === uid && !isExplicitlyImpersonating();
    pending.current = true; setBusy(true); setError('');
    try { await action(isCurrent); }
    catch (failure) {
      if (isCurrent()) {
        setError(getEmailChangeCodeError(failure));
        if (failure.retryAt) setRetryAt(failure.retryAt);
      }
    } finally {
      if (generation.current === id) { pending.current = false; setBusy(false); setNow(Date.now()); }
    }
  };
  const send = event => {
    event?.preventDefault();
    if (resendIn || verified) return;
    const target = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) { setError('invalid_email'); return; }
    return run(async isCurrent => {
      const metadata = await requestEmailChangeCode(target, language, uid);
      if (isCurrent()) { setChallenge({ ...metadata, email: target }); setEmail(target); setCode(''); setRetryAt(0); }
    });
  };
  const verify = event => {
    event.preventDefault();
    if (!challenge || (!verified && !/^\d{6}$/.test(code))) return;
    if (!verified && Date.parse(challenge.expiresAt) <= Date.now()) { setError('email_code_expired'); return; }
    return run(async isCurrent => {
      await auth.completeEmailCodeChange({
        verify: () => verifyEmailChangeCode(challenge.challengeId, code, uid),
        email: challenge.email, uid, isCurrent,
        recovery: recovery.current,
        onVerified: () => { if (isCurrent()) setVerified(true); },
      });
      if (isCurrent()) onClose();
    });
  };
  const edit = () => {
    if (!pending.current && !verified) {
      setRetryAt(Math.max(Date.parse(challenge?.resendAt) || 0, retryAt));
      setChallenge(null); setCode(''); setError('');
    }
  };
  return { email, setEmail, code, setCode, challenge, error, busy, resendIn, expired, blocked, verified, send, verify, edit };
}
